import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { colors, borderRadius } from '../styles/designTokens';
import type { DashboardKeywordListItem } from '../models/DashboardModels';
import { Table, TableHeader, TableSection } from '../styles/DashboardStyles';

export type DashboardKeywordTableVariant = 'active' | 'inactive';

interface DashboardKeywordTableProps {
  items: DashboardKeywordListItem[];
  variant: DashboardKeywordTableVariant;
}

const DetailButton = styled.button`
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.primary[500]};
  background-color: ${colors.primary[50]};
  border: 1px solid ${colors.primary[200]};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${colors.primary[100]};
    border-color: ${colors.primary[300]};
    color: ${colors.primary[600]};
  }
`;

const UtilBadge = styled.span<{ $on: boolean }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${borderRadius.base};
  font-size: 12px;
  font-weight: 700;
  ${({ $on }) =>
    $on
      ? `
    background: ${colors.success[100]};
    color: ${colors.success[800]};
  `
      : `
    background: ${colors.error[50]};
    color: ${colors.error[800]};
  `}
`;

const StyledTable = styled(Table)`
  td:nth-child(1),
  th:nth-child(1) {
    width: 10%;
  }
  td:nth-child(2),
  th:nth-child(2) {
    width: 10%;
  }
  td:nth-child(3),
  th:nth-child(3) {
    width: 14%;
  }
  td:nth-child(4),
  th:nth-child(4) {
    width: 10%;
  }
  td:nth-child(5),
  th:nth-child(5) {
    width: 8%;
  }
  td:nth-child(6),
  th:nth-child(6) {
    width: 7%;
    text-align: center;
  }
  td:nth-child(7),
  th:nth-child(7) {
    width: 8%;
    text-align: right;
  }
  td:nth-child(8),
  th:nth-child(8) {
    width: 9%;
    text-align: center;
  }
  td:nth-child(9),
  th:nth-child(9) {
    width: 10%;
    text-align: center;
  }
  td:nth-child(10),
  th:nth-child(10) {
    width: 10%;
    text-align: center;
  }
`;

const titles: Record<DashboardKeywordTableVariant, string> = {
  active: '활성 키워드 목록',
  inactive: '비활성 키워드 목록'
};

const DashboardKeywordTable: React.FC<DashboardKeywordTableProps> = ({ items, variant }) => {
  const navigate = useNavigate();
  const showUtil = variant === 'active';

  return (
    <TableSection>
      <TableHeader>
        <h3>{titles[variant]}</h3>
      </TableHeader>
      <StyledTable>
        <thead>
          <tr>
            <th>고객</th>
            <th>아이디</th>
            <th>키워드</th>
            <th>브랜드</th>
            <th>사이트</th>
            <th>주기</th>
            <th>유형</th>
            <th>잔여 P</th>
            {showUtil ? <th>활용 가능</th> : null}
            <th>상세</th>
          </tr>
        </thead>
        <tbody>
          {items.map(row => {
            const utilOn = row.잔여포인트 >= 0;
            return (
              <tr key={`${row.user_idx}-${row.keyword_idx}`}>
                <td>{row.고객명}</td>
                <td>{row.아이디}</td>
                <td>{row.키워드}</td>
                <td>{row.브랜드명}</td>
                <td>{row.타겟사이트}</td>
                <td>{row.수집주기}</td>
                <td>{row.분석유형}</td>
                <td>{row.잔여포인트.toLocaleString('ko-KR')}</td>
                {showUtil ? (
                  <td>
                    <UtilBadge $on={utilOn}>{utilOn ? 'ON' : 'OFF'}</UtilBadge>
                  </td>
                ) : null}
                <td>
                  <DetailButton type="button" onClick={() => navigate(`/customers/${row.user_idx}`)}>
                    보기
                  </DetailButton>
                </td>
              </tr>
            );
          })}
          {items.length === 0 && (
            <tr>
              <td colSpan={showUtil ? 10 : 9} style={{ textAlign: 'center', padding: '20px' }}>
                표시할 키워드가 없습니다.
              </td>
            </tr>
          )}
        </tbody>
      </StyledTable>
    </TableSection>
  );
};

export default DashboardKeywordTable;
